import { clamp, min } from 'lodash-es';
import { type IAnalyserNode, type IAudioContext } from 'standardized-audio-context';
import { type ChunkSpec } from '../../types';
import { logger } from './logger';

type SilenceCallbackReason = 'silence' | 'max_duration';

export class SilenceDetector {
  private readonly minSilenceDuration = 300;
  private readonly samplingRate = 40;
  private readonly thresholdBuffer = 10;
  private readonly defaultMinDuration = 10000;
  private readonly defaultMaxDuration = 30000;

  private micDataArray: Uint8Array | null = null;
  private tabDataArray: Uint8Array | null = null;
  private silenceStart: number | null = null;
  private silenceCallback: (() => void) | null = null;
  private noiseFloorCallback: ((noiseFloor: number) => void) | null = null;
  private recordingStartTime: number = 0;
  private silenceTimeoutId: NodeJS.Timeout | null = null;
  private durationTimeoutId: NodeJS.Timeout | null = null;
  private minDuration: number;
  private maxDuration: number;
  private noiseFloor: number = Infinity;
  public noiseHistory: number[] = [];
  public noiseFloorHistory: number[] = [];
  private tickIndex: number = 0;
  private seenSound: boolean = false;
  private running: boolean = false;
  constructor(
    private chunkSpec: ChunkSpec,
    private micAnalyzer: IAnalyserNode<IAudioContext>,
    private tabAnalyzer?: IAnalyserNode<IAudioContext>
  ) {
    const chunkDuration = this.chunkSpec.chunkDurations[0];
    this.minDuration = chunkDuration
      ? chunkDuration.minDuration
      : this.defaultMinDuration;
    this.maxDuration = chunkDuration
      ? chunkDuration.maxDuration
      : this.defaultMaxDuration;
  }

  onSilenceDetected(callback: () => void): void {
    this.silenceCallback = callback;
  }

  onNoiseFloorUpdate(callback: (noiseFloor: number) => void): void {
    this.noiseFloorCallback = callback;
  }

  start() {
    this.running = true;
    this.recordingStartTime = Date.now();
    this.setupAudioAnalysis();
    this.detectSilence();
    this.checkMaxDuration();
  }

  updateDurations(index: number) {
    if (index >= this.chunkSpec.chunkDurations.length) {
      index = this.chunkSpec.chunkDurations.length - 1;
    }
    const chunkDuration = this.chunkSpec.chunkDurations[index];
    this.minDuration = chunkDuration
      ? chunkDuration.minDuration
      : this.defaultMinDuration;
    this.maxDuration = chunkDuration
      ? chunkDuration.maxDuration
      : this.defaultMaxDuration;
  }

  stop() {
    this.running = false;
    // Clear the timeouts to prevent callbacks after stopping
    if (this.silenceTimeoutId !== null) {
      clearTimeout(this.silenceTimeoutId);
      this.silenceTimeoutId = null;
    }

    if (this.durationTimeoutId !== null) {
      clearTimeout(this.durationTimeoutId);
      this.durationTimeoutId = null;
    }

    this.micDataArray = null;
    this.tabDataArray = null;
  }

  private setupAudioAnalysis() {
    const micBufferLength = this.micAnalyzer.frequencyBinCount;
    this.micDataArray = new Uint8Array(micBufferLength);
    if (this.tabAnalyzer) {
      const tabBufferLength = this.tabAnalyzer.frequencyBinCount;
      this.tabDataArray = new Uint8Array(tabBufferLength);
    }
  }

  private detectSilence() {
    if (
      !this.running ||
      !this.micAnalyzer ||
      !this.micDataArray ||
      !this.silenceCallback
    ) {
      return;
    }

    try {
      this.micAnalyzer.getByteFrequencyData(this.micDataArray);

      if (this.tabAnalyzer && this.tabDataArray) {
        this.tabAnalyzer.getByteFrequencyData(this.tabDataArray);
      }

      const maxBinCount = Math.max(
        this.micDataArray.length,
        this.tabDataArray?.length ?? 0
      );
      const combinedDataArray = new Uint8Array(maxBinCount);
      for (let i = 0; i < maxBinCount; i++) {
        const micFrequency = this.micDataArray[i] ?? 0;
        const tabFrequency = this.tabDataArray?.[i] ?? 0;
        let combinedFrequency = micFrequency + tabFrequency;
        if (combinedFrequency > 255) combinedFrequency = 255;
        combinedDataArray[i] = combinedFrequency;
      }

      // Calculate RMS (Root Mean Square) for more accurate audio loudness measurement
      const rms = Math.sqrt(
        Array.from(combinedDataArray).reduce((sum, value) => sum + value * value, 0) /
          combinedDataArray.length
      );

      const currentDuration = Date.now() - this.recordingStartTime;
      const isSilent = rms < this.noiseFloor + this.thresholdBuffer;
      this.updateNoiseFloor(rms);

      if (isSilent) {
        if (this.silenceStart === null) {
          this.silenceStart = Date.now();
        } else if (
          Date.now() - this.silenceStart >= this.minSilenceDuration &&
          currentDuration >= this.minDuration &&
          this.seenSound
        ) {
          try {
            this.invokeSilenceCallback('silence');
          } finally {
            this.recordingStartTime = Date.now();
            this.silenceStart = null;
            this.seenSound = false;
          }
        }
      } else {
        this.silenceStart = null;
        this.seenSound = true;
      }
    } finally {
      if (this.running) {
        // Store the timeout ID so we can clear it when stopping
        this.silenceTimeoutId = setTimeout(() => this.detectSilence(), this.samplingRate);
        this.tickIndex++;
      }
    }
  }

  private updateNoiseFloor(level: number) {
    this.noiseHistory.push(level);
    const currentTime = Date.now() - this.recordingStartTime;
    this.noiseHistory = this.noiseHistory.slice(-2000);

    // Use at most the last 20% of the current runtime worth of samples.
    const historyToConsider = this.noiseHistory.slice(
      (-1 * currentTime) / 5 / this.samplingRate
    );

    if (!historyToConsider.length) return;
    this.noiseFloor = min(historyToConsider) ?? 0;
    this.noiseFloorHistory.push(this.noiseFloor);
    this.noiseFloorHistory = this.noiseFloorHistory.slice(-2000);
    this.noiseFloorCallback?.(this.noiseFloor);
  }

  private checkMaxDuration() {
    if (!this.running || !this.silenceCallback) return;

    try {
      const currentDuration = Date.now() - this.recordingStartTime;

      if (
        currentDuration >=
        clamp(this.maxDuration - this.samplingRate, this.samplingRate, Infinity)
      ) {
        try {
          this.invokeSilenceCallback('max_duration');
        } finally {
          this.recordingStartTime = Date.now();
        }
      }
    } finally {
      if (this.running) {
        this.durationTimeoutId = setTimeout(
          () => this.checkMaxDuration(),
          this.samplingRate
        );
      }
    }
  }

  private invokeSilenceCallback(reason: SilenceCallbackReason) {
    if (!this.silenceCallback) return;

    try {
      this.silenceCallback();
    } catch (err) {
      const now = Date.now();
      logger.error({
        event_type: 'SILENCE_DETECTOR_CALLBACK_FAILED',
        details: {
          reason,
          error: err,
          recordingDurationMs: now - this.recordingStartTime,
          silenceDurationMs: this.silenceStart === null ? null : now - this.silenceStart,
          minDurationMs: this.minDuration,
          maxDurationMs: this.maxDuration,
          running: this.running,
        },
      });
      throw err;
    }
  }
}
